import { DocumentEvents } from "./../../helpers/document-events";
import { FullTextMode } from "./../../models/tabs/judgment-tab-state";
import { Container } from "aurelia-dependency-injection";
import { FulltextTabModel } from "./../../models/tabs/fulltext";
import { GenericTabView } from "./generic-tab";
import { AppEvents } from "./../../../app-events";
import { AppSettings } from "../../../app-settings";
import { ContainerHelper } from "../../../shared/helpers/container-helper";

const dom = {
    FULL_TEXT_DOCUMENT: ".full-text-document",
    FULLTEXT_LOADING: ".fulltext-loading",
    COLLAPSE_PARA_NUM: ".para-num.collapsable",
    OPINION_GROUP: ".opinion-group",

    CONTEXT_BUTTONS: ".context-link",
    CONTEXT_BUTTONS_BLANK: ".no-context-link",
    PARAGRAPH_GROUP: ".para-group",
    FULL_TEXT_CONTENT: ".full-text-content",
    SEARCH_TERM_PREV: ".search-term-up",
    SEARCH_TERM_NEXT: ".search-term-down",
    PARAGRAPH_STEPPERS: ".paragraph-step",
    PARAGRAPH_NUMBER_TEXT: ".txt-paragraph-number",
    HIGHLIGHTED_PARAGRAPHS: "[class*='paragraph-highlight-']",
    HIGHLIGHT_BUTTON: ".highlight-button",
    HIGHLIGHT_CONTROLS: ".highlight-controls",
    CLEAR_FILTERS: ".clear-filters",
    FOOTNOTE_LINK: ".footnote-ref",
    FULLTEXT_NAV_LOADING: ".fulltext-nav-loading",
    HIGHLIGHTED_TEXT: ".text-highlight",
    PARAGRAPH_FILTER_BANNER: ".paragraph-filter-banner",
    PARAGRAPH_FILTER_BANNER_TEXT: ".paragraph-filter-banner-text",
    DOCUMENT_PDF_MODE_BUTTON: ".document-pdf-button",
    FULLSCREEN_READER_BUTTON: ".fullscreen-reader-button",
    FULLSCREEN_READER_NEW_TAB_BUTTON: "#read-mode-open-new-tab",
    DOCUMENT_PDF_SELECT: ".document-pdf-select",
    JUDGE_HEADING: ".judge-heading",
    JUDGMENT_HEADER: ".judgment-header",
    JUDGMENT_HEADING: ".full-text .heading"
};

const scrollPosition = null;
const noFullTextSorryMessage = `<p class="no-full-text">There is no full text available for this document</p>`;

export abstract class FulltextTabView extends GenericTabView {

    public newTab: Window;
    public model: FulltextTabModel;
    protected waypoints: any;
    protected appEvents: Backbone.Events;
    protected paragraphTimer: any;

    public constructor(options?: any) {
        super(options);
        this.appEvents = ContainerHelper.Events;
    }

    public onShow() {
        this.model.State.FullTextMode = FullTextMode.Text;
        this.renderFullText(this.model.Data.FullText);
        this.setupModelListeners();
        this.setupPdfButtons();
    }

    public setupModelListeners() {
        this.listenTo(this.model.State, "change:FullTextMode", this.fullTextModeChanged);
        this.listenTo(this.model.Data, DocumentEvents.RequestConsideredCICCounts, this.consideredCICCountsRequested);
    }

    public events(): Backbone.EventsHash {
        const events: Backbone.EventsHash = {};
        events["click " + dom.FULLSCREEN_READER_BUTTON] = (e) => {
            this.closeReadModeDropdown();
            this.fullscreenReaderButtonClick(e);
        };
        events["contextmenu " + dom.FULLSCREEN_READER_BUTTON] = (e) => {
            e.preventDefault();
            this.toggleReadModeDropdown();
        };
        events["click " + dom.FULLSCREEN_READER_NEW_TAB_BUTTON] = (e) => {
            this.toggleReadModeDropdown();
            this.fullscreenReaderNewTabButtonClick(e);
        };
        events["click " + dom.DOCUMENT_PDF_MODE_BUTTON] = (e) => this.documentPDFClick(e);
        events["click " + dom.DOCUMENT_PDF_SELECT] = (e) =>  this.pdfSelectClick(e);

        events[ "mousewheel " + dom.FULL_TEXT_CONTENT] = (e) => this.hideFooterPopover(e);
        events[ "DOMMouseScroll " + dom.FULL_TEXT_CONTENT] = (e) => this.hideFooterPopover(e);
        return events;
    }

    protected consideredCICCountsRequested(data) {
        const counts = [];
        _.each(data.ids, (id: string) => {
            counts.push({id: id, count: this.getLinksWithRelID(id)});
        });
        this.model.Data.trigger(DocumentEvents.CICCountsReady, {tabid: data.tabid, counts: counts});
    }

    /** Finds the count of links with the rel-id attribute pointing to a specific document for Citations in Context
     * @param {string} relID The contents of the rel-id attribute that should be found
     */
    protected getLinksWithRelID(relID: string): number {
        return this.$(`a[data-id="${relID}"]`).length;
    }

    protected fullTextModeChanged() {
        const mode = this.model.State.FullTextMode;
        const content = this.$(".full-text-content.scrollable");
        const fullText = this.$(".full-text-document");
        const pdfHolder = this.$(".full-text-pdf");
        const paragraphControls = this.$(".paragraph-controls");
        const highlightControls = this.$(".highlight-controls");
        const pdfButton = this.$(".show-pdf");
        const textButton = this.$(".show-text");
        const pdfControls = this.$(".document-pdf-selectors");
        const scrollControls = this.$(".judgment-scroll-selectors");
        const headerContentControls = this.$(".header-content-controls");

        if (mode === FullTextMode.Pdf) {
            fullText.hide().removeClass("show");
            highlightControls.hide();
            paragraphControls.hide();
            pdfControls.show();
            scrollControls.hide();
            headerContentControls.hide();
            textButton.show();
            pdfButton.hide();
            pdfHolder.removeClass("report-pdf").addClass("document-pdf").removeClass("hide").show();
            this.$(content).addClass("pdf-mode");
            this.$(".document-pdf-select").first().trigger("click");
            this.$(dom.DOCUMENT_PDF_MODE_BUTTON).attr("title", "View the documents's Text");
        } else {
            fullText.show();
            highlightControls.show();
            paragraphControls.show();
            pdfControls.hide();
            scrollControls.show();
            this.model.State.ShowHeaderControls ? headerContentControls.show() : headerContentControls.hide();
            pdfHolder.hide().addClass("hide");
            textButton.hide();
            pdfButton.show();
            this.$(dom.DOCUMENT_PDF_MODE_BUTTON).attr("title", "View the documents's PDF");
            this.$(content).removeClass("pdf-mode");
        }
    }

    protected renderFullText(content: string) {
        if (!content && this.model.Data.DocumentAccessSku.toLowerCase() === "pro") { // if you're not pro there will be no fulltext but leave this instead the promo material will show
            content  = noFullTextSorryMessage;
        }
        this.$(dom.FULL_TEXT_DOCUMENT).html(content);

        _.defer(() => {
            window.addEventListener("message", (e) => this.receieveMessage(e), false);
            if (window.opener) {
                window.opener.postMessage({message: "FullScreenRequest"}, this.newTab);
            }
        });
    }

    protected fullscreenReaderButtonClick(e) {
        this.appEvents.trigger(AppEvents.AppInsightsDocumentReadMode, {RenderType: this.model.Data.RenderType});

        _.defer(() => {
            const visible = this.$(".fulltext-section:visible");
            const clone = visible.clone();
            this.appEvents.trigger("FullScreen", clone);
        });
    }

    protected fullscreenReaderNewTabButtonClick(e) {
        this.appEvents.trigger(AppEvents.AppInsightsDocumentReadMode, {RenderType: this.model.Data.RenderType});
        this.newTab = window.open(Backbone.history.getFragment());
        this.newTab.focus();
    }

    protected receieveMessage(event) {
        if (event.origin !== AppSettings.WindowOrigin) {
            return;
        }

        if (event.data.message === "FullScreen") {
            this.model.State.FullTextMode = event.data.fullTextMode;
            _.delay(() => {
                const visible = this.$(".fulltext-section:visible");
                const clone = visible.clone();
                this.appEvents.trigger("FullScreen", clone);
                return;
            }, 1000);
        }

        if (event.data.message === "FullScreenRequest" && this.newTab) {
            this.newTab.postMessage({message: "FullScreen", fullTextMode: this.model.State.FullTextMode}, window.opener);
            this.newTab = null;
            return;
        }
    }

    protected documentPDFClick(e: JQueryEventObject): void {
        $(e.currentTarget).blur();
        e.stopPropagation();
        if ($(e.currentTarget).hasClass("disabled")) {
            return;
        }
        if (this.model.State.FullTextMode === FullTextMode.Text) {
            this.switchToPdfViewMode();
        } else {
            this.switchToTextViewMode();
        }
        this.appEvents.trigger(AppEvents.AppInsightsDocumentPDFView, {MaterialType: "Case"});
    }

    protected setupPdfButtons() {
        const numPdfs = this.model.Data.PdfLinks.length;

        this.$(dom.DOCUMENT_PDF_MODE_BUTTON).removeClass("disabled").attr("title", "View the judgment's PDF");
        const $group = $("<li class='document-pdf-selectors'><div class='btn-group'></div></li>");

        if (numPdfs > 0 && numPdfs < 5) {
            _.each(this.model.Data.PdfLinks, (link, idx) => {
                const button = `<button class="btn document-pdf-select" data-pdf="${link.PdfLink}"><i class="fa fa-file-pdf-o"></i> PDF #${idx + 1}</button>`;
                $group.find(".btn-group").append(button);
            });
        } else if (numPdfs >= 5) {
            _.each(this.model.Data.PdfLinks, (link, idx) => {
                const button = `<button class="btn document-pdf-select" data-pdf="${link.PdfLink}"><i class="fa fa-file-pdf-o"></i> #${idx + 1}</button>`;
                $group.find(".btn-group").append(button);
            });
        } else {
            const button = '<button class="btn document-pdf-select" data-pdf="Generate"><i class="fa fa-file-pdf-o"></i>PDF #1</button>';
            $group.find(".btn-group").append(button);
        }
        this.$(".fulltext-section.document-section").find(".nav").prepend($group);

        // If there is a pdf with no full text then go straight to the pdf
        if (numPdfs > 0 && !this.model.Data.HasFullText) {
            this.switchToPdfViewMode();
        }
    }

    protected switchToPdfViewMode(): void {
        this.$(dom.PARAGRAPH_FILTER_BANNER).hide();
        this.model.State.ScrollPosition = this.$(".full-text-content.scrollable").scrollTop();
        this.model.State.FullTextMode = FullTextMode.Pdf;
    }

    protected switchToTextViewMode(): void {
        if (this.model.State.FullTextMode !== FullTextMode.Text) {
            if (this.$(dom.PARAGRAPH_FILTER_BANNER_TEXT).text().trim().length) {
                this.$(dom.PARAGRAPH_FILTER_BANNER).show();
            }
            this.$(".full-text-content.scrollable").css("z-index", "10");
            this.model.State.FullTextMode = FullTextMode.Text;
            this.$(".full-text-content.scrollable").scrollTop(scrollPosition);

        }
    }

    protected pdfSelectClick(e: JQueryEventObject): void {
        const $button = $(e.currentTarget);
        this.$(".document-pdf-select").not($button).removeClass("disabled");
        $button.addClass("disabled");
        const pdfLink = $button.attr("data-pdf");
        this.loadDocumentPdf(pdfLink);
    }

    // The pdf reader needs to refresh the access token so that it can get the pdf in the link
    protected abstract loadDocumentPdf(pdfLink: string): Promise<any>;

    protected hideFooterPopover(popover: JQueryEventObject) {
        this.$(dom.FOOTNOTE_LINK).each((idx, ele) => {
            $(ele).popover("hide");
        });
    }

    protected toggleReadModeDropdown() {
        this.$("#read-mode").toggle();

        $(window.document).click(() => {
            this.closeReadModeDropdown();
        });
    }

    protected closeReadModeDropdown() {
        this.$("#read-mode").hide();
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader??ref--0!./src/desktop/views/tabs/fulltext-tab.ts